// ── Parts picker tags (pure) ────────────────────────────────────────────────
// Every card in the diagram's parts list carries ONE row of tags, of three
// types:
//   • Functionality — exactly one per part: what the part IS ("Face bumper",
//     "Hose"). Green when it is what the customer asked for, light red when
//     it isn't. On a hose order every hose is green and everything else red.
//   • Features — keyless, high pressure, complete assembly… plus the AI
//     comparison's distinguishing values the catalog didn't state.
//   • Position — left, right, both sides, front, rear, upper, lower, outer…
// plus the numbered answer option the card satisfies ("① Base / bracket only")
// and, on a kit's header, the kit itself ("Kit · 3 parts").
//
// Rules applied here so every surface gets them for free:
//   1. no tag twice on one part (an AI "Left" is the position tag, not a
//      second chip; an answer that names a feature replaces that feature);
//   2. a kit's header carries every tag true of the whole kit, and the part
//      rows under it never repeat those;
//   3. filters are the positive tags only (never a red functionality tag),
//      and a tag every card wears filters nothing, so it is not offered.

import { positionChipsFromExtra, positionClassesFromName } from './partLabel';

export type FeatureKey = 'assembly' | 'kitPart' | 'keyless' | 'highPressure' | 'heated' | 'folding';
export type PositionKey = 'left' | 'right' | 'bothSides' | 'front' | 'rear' | 'upper' | 'lower' | 'outer' | 'inner';

export type PartTag =
  /** `value` is what the part is (display text). `match` = what was asked for.
   *  `includes` (kit headers only): the kit is not the part, but contains it. */
  | { kind: 'function'; key: string; match: boolean; value: string; includes?: boolean }
  /** Vocabulary feature (`key` = FeatureKey) or an AI value (`key` = "ai:…", `value` set). */
  | { kind: 'feature'; key: string; value?: string }
  | { kind: 'position'; key: PositionKey }
  | { kind: 'answer'; key: string; n: number; value: string }
  | { kind: 'kit'; key: 'kit'; n: number };

/** Accent- and case-folded, punctuation as spaces, padded with spaces so
 *  whole-word matching on " word " works in every catalog language. */
export const foldText = (s: string | null | undefined): string => ` ${String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;

/** Each tag's words (folded). A trailing `*` matches a word prefix. */
export const FEATURE_WORDS: Record<FeatureKey, string[]> = {
  assembly: ['assembly', 'assy', 'complete', 'completo', 'completa', 'conjunto', 'cpl', 'compl'],
  kitPart: ['kit', 'juego', 'jogo', 'repair set'],
  keyless: ['keyless', 'sin llave', 'sem chave', 'without key'],
  highPressure: ['high pressure', 'alta presion', 'alta pressao'],
  heated: ['heated', 'heating', 'calefactado', 'calefaccionado', 'termico', 'aquecido'],
  folding: ['folding', 'foldable', 'abatible', 'plegable', 'rebatible', 'rebativel'],
};

export const POSITION_WORDS: Record<PositionKey, string[]> = {
  left: ['left', 'lh', 'izq*', 'esquerd*'],
  right: ['right', 'rh', 'derech*', 'direit*'],
  bothSides: ['both sides', 'both', 'ambos', 'ambos lados', 'ambos os lados', 'ambas', 'left and right', 'lh rh', 'izquierdo y derecho'],
  front: ['front', 'frt', 'delanter*', 'dianteir*'],
  rear: ['rear', 'trasero', 'trasera', 'traseir*', 'posterior'],
  upper: ['upper', 'top', 'superior'],
  lower: ['lower', 'bottom', 'inferior'],
  outer: ['outer', 'outside', 'exterior', 'externo', 'externa'],
  inner: ['inner', 'inside', 'interior', 'interno', 'interna'],
};

export const FEATURE_ORDER: FeatureKey[] = ['assembly', 'kitPart', 'keyless', 'highPressure', 'heated', 'folding'];
export const POSITION_ORDER: PositionKey[] = ['left', 'right', 'bothSides', 'front', 'rear', 'upper', 'lower', 'outer', 'inner'];

const hasWord = (folded: string, w: string): boolean =>
  w.endsWith('*') ? folded.includes(` ${w.slice(0, -1)}`) : folded.includes(` ${w} `);
export const mentions = (folded: string, list: string[]): boolean => list.some(w => hasWord(folded, w));

const isYes = (v: unknown) => /^(yes|true|1|y|si|sim)$/i.test(String(v ?? '').trim());

export const featuresOf = (texts: Array<string | null | undefined>, extra?: Record<string, unknown> | null): FeatureKey[] => {
  const folded = foldText(texts.filter(Boolean).join(' '));
  const found = new Set<FeatureKey>(FEATURE_ORDER.filter(k => mentions(folded, FEATURE_WORDS[k])));
  // A component of a kit (the kit SKU itself wears the kit tag instead).
  if (isYes((extra as any)?.part_of_building_kit)) found.add('kitPart');
  return FEATURE_ORDER.filter(k => found.has(k));
};

export const positionsOf = (texts: Array<string | null | undefined>, extra?: Record<string, unknown> | null): PositionKey[] => {
  const chips = positionChipsFromExtra(extra as any);
  const all = [...texts, ...chips].filter(Boolean) as string[];
  const folded = foldText(all.join(' '));
  const found = new Set<PositionKey>(POSITION_ORDER.filter(k => mentions(folded, POSITION_WORDS[k])));
  for (const t of all) {
    const c = positionClassesFromName(t);
    if (c.side) found.add(c.side);
    if (c.position) found.add(c.position);
  }
  return normalizePositions(found);
};

const normalizePositions = (found: Set<PositionKey>): PositionKey[] => {
  if (found.has('left') && found.has('right')) found.add('bothSides');
  if (found.has('bothSides')) { found.delete('left'); found.delete('right'); }
  if (found.has('front') && found.has('rear')) { found.delete('front'); found.delete('rear'); }
  if (found.has('upper') && found.has('lower')) { found.delete('upper'); found.delete('lower'); }
  if (found.has('outer') && found.has('inner')) { found.delete('outer'); found.delete('inner'); }
  return POSITION_ORDER.filter(k => found.has(k));
};

// ── Functionality ───────────────────────────────────────────────────────────

const CONNECTORS = new Set(['de', 'del', 'da', 'do', 'dos', 'das', 'para', 'con', 'com', 'en', 'em', 'of', 'for', 'with', 'the', 'a', 'y', 'e', 'la', 'el', 'los', 'las']);
const ROMANCE = new Set(['de', 'del', 'da', 'do', 'dos', 'das', 'para', 'con', 'com']);
/** One word that is nothing but a side / position ("Right", "izquierdo") —
 *  not "rear-view", which names a kind of mirror. */
const isPositionWord = (w: string) => {
  const f = foldText(w).trim();
  return !!f && !f.includes(' ') && Object.values(POSITION_WORDS).some(list => mentions(` ${f} `, list));
};
const sentence = (s: string) => { const t = s.trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : t; };

/** What a catalog name says the part IS: its head (before the first comma or
 *  bracket) without side/position words. "MIRROR, OUTER Right side" → "Mirror";
 *  "Base De Espejo Retrovisor, Ambos, Exterior" → "Base de espejo retrovisor". */
export const functionOf = (name: string | null | undefined): string => {
  const head = String(name || '').split(/[,(;/]/)[0].trim();
  // Leading position words go ("Front bumper cover" → "Bumper cover"); the
  // first one after the part's own words ends it ("Strut outside mirror" →
  // "Strut": what follows says where it sits, not what it is).
  let words = head.split(/\s+/).filter(Boolean);
  // Catalog style "HINGE ASSY-FRONT DOOR": the part is what comes before ASSY.
  const assy = words.findIndex(w => ['assy', 'asm'].includes(foldText(w).trim().split(' ')[0]));
  if (assy > 0) words = words.slice(0, assy);
  let i = 0;
  while (i < words.length && isPositionWord(words[i])) i += 1;
  const kept: string[] = [];
  for (; i < words.length && !isPositionWord(words[i]); i += 1) kept.push(words[i]);
  // Drop dangling connectors left behind ("Espejo de" after stripping "lado").
  while (kept.length && CONNECTORS.has(foldText(kept[kept.length - 1]).trim())) kept.pop();
  return sentence(kept.join(' ') || head);
};

/** The head noun: first content word in Romance-language names ("manguera de
 *  radiador" → manguera), last in English ones ("radiator hose" → hose). */
const ASSEMBLY_WORDS = new Set(['assy', 'assembly', 'asm', 'assembled']);
const headNoun = (fn: string): string => {
  let ws = foldText(fn).trim().split(' ').filter(Boolean);
  // Catalog style "HINGE ASSY-FRONT DOOR": the part is what comes before
  // ASSY — a hinge for the front door, not a door.
  const at = ws.findIndex(w => ASSEMBLY_WORDS.has(w));
  if (at > 0) return ws[at - 1];
  ws = ws.filter(w => !ASSEMBLY_WORDS.has(w));
  const content = ws.filter(w => !CONNECTORS.has(w));
  if (!content.length) return '';
  return ws.some(w => ROMANCE.has(w)) || content.length === 1 ? content[0] : content[content.length - 1];
};
const stem = (w: string) => (w.length > 5 ? w.slice(0, 5) : w);

/** Same kind of part? Compared on the head noun, so on a hose order every hose
 *  matches and a clamp doesn't. Different languages simply don't match (the
 *  caller's candidate flag still marks those parts). */
export const sameFunction = (requested: string | null | undefined, own: string | null | undefined): boolean => {
  const a = headNoun(functionOf(requested));
  const b = headNoun(functionOf(own));
  return !!a && !!b && stem(a) === stem(b);
};

/** Does the name mention the requested kind anywhere ("PANEL ASSY-FRONT
 *  DOOR" mentions door)? Weaker than sameFunction: only good enough for a
 *  part the search already offered as a candidate. */
export const mentionsFunction = (requested: string | null | undefined, name: string | null | undefined): boolean => {
  const a = headNoun(functionOf(requested));
  if (!a) return false;
  return foldText(name).trim().split(' ').some(w => w.length >= 3 && stem(w) === stem(a));
};

// ── Tags of one card ────────────────────────────────────────────────────────

export const tagId = (t: PartTag): string => {
  switch (t.kind) {
    case 'function': return `function:${t.match ? (t.includes ? 'includes' : 'match') : 'no'}:${foldText(t.value).trim()}`;
    case 'feature': return `feature:${t.key}`;
    case 'position': return `position:${t.key}`;
    case 'answer': return `answer:${t.key}`;
    case 'kit': return 'kit';
    default: return '';
  }
};

/** A red functionality tag says what the part is NOT; it is never a filter. */
export const isNegativeTag = (t: PartTag): boolean => t.kind === 'function' && !t.match;

export interface AiRowTags {
  /** Distinguishing values ("Finish: Painted", "Keyless"). */
  features?: string[];
  side?: 'left' | 'right' | null;
  position?: 'front' | 'rear' | null;
}

/** The backend's reading of a catalog row (catax_ai.bl.axes vocabulary,
 *  read once per distinct string by a model, in any language or format).
 *  When present it is THE source of the row's side / position tags; the word
 *  lists below are only the fallback for a row not read yet. */
export interface RowAxes {
  side?: 'left' | 'right' | 'both' | null;
  position?: 'front' | 'rear' | 'both' | null;
  height?: 'upper' | 'lower' | null;
  placement?: 'inner' | 'outer' | null;
  component?: string | null;
}

/** The position tags a row's axes state. */
export const positionsFromAxes = (axes: RowAxes | null | undefined): PositionKey[] => {
  if (!axes) return [];
  const found = new Set<PositionKey>();
  if (axes.side === 'left' || axes.side === 'right') found.add(axes.side);
  if (axes.side === 'both') found.add('bothSides');
  if (axes.position === 'front' || axes.position === 'rear') found.add(axes.position);
  if (axes.height === 'upper' || axes.height === 'lower') found.add(axes.height);
  if (axes.placement === 'inner' || axes.placement === 'outer') found.add(axes.placement);
  return POSITION_ORDER.filter(k => found.has(k));
};

export interface CardTagInput {
  /** Catalog name, its translation, description — what features/positions are read from. */
  texts: Array<string | null | undefined>;
  extra_details?: Record<string, unknown> | null;
  /** The backend's reading of the row; positions come from here when set. */
  axes?: RowAxes | null;
  /** What this part is (display text, usually functionOf(translated name)). */
  functionName: string;
  /** What the customer asked for (display text); green tags show this. */
  requestedType?: string | null;
  isRequested: boolean;
  answer?: { n: number; label: string } | null;
  ai?: AiRowTags | null;
  /** The part IS a kit (a kit SKU): it wears "Kit", not "Kit part". */
  isKit?: boolean;
}

const splitAiTag = (tag: string): { label: string; value: string } => {
  const text = String(tag || '').replace(/\s+/g, ' ').trim();
  const at = text.indexOf(':');
  if (at <= 0 || at === text.length - 1) return { label: '', value: text };
  return { label: text.slice(0, at).trim(), value: text.slice(at + 1).trim() };
};

/** The vocabulary key an AI value restates, if any ("Left side" → left). */
type VocabTag = { kind: 'feature'; key: FeatureKey } | { kind: 'position'; key: PositionKey };
// Words that carry no fact of their own in a side / position phrase.
const FILLER = new Set(['side', 'sides', 'lado', 'lados', 'lato', 'only', 'solo', 'and', 'y', 'e', 'de', 'del', 'da', 'do', 'of', 'the']);
const wordMatches = (w: string, entry: string) => (entry.endsWith('*') ? w.startsWith(entry.slice(0, -1)) : w === entry);

/** The vocabulary tags a value is made of — ONLY when every word in it is a
 *  side / position / feature word (or filler): "Left front" → left + front,
 *  "Both sides, heated" → both sides + heated. Any other word ("Left hand
 *  drive only", "Complete mirror") makes it a fact of its own: null. Works
 *  for any combination and language the word lists know — nothing is listed
 *  per combination. */
export const vocabularyTagsOf = (value: string): VocabTag[] | null => {
  let f = foldText(value);
  const positions = new Set<PositionKey>();
  const features = new Set<FeatureKey>();
  // Phrases first ("both sides", "high pressure"), so their words count once.
  for (const k of POSITION_ORDER) for (const w of POSITION_WORDS[k]) if (w.includes(' ') && f.includes(` ${w} `)) { positions.add(k); f = f.split(` ${w} `).join(' '); }
  for (const k of FEATURE_ORDER) for (const w of FEATURE_WORDS[k]) if (w.includes(' ') && f.includes(` ${w} `)) { features.add(k); f = f.split(` ${w} `).join(' '); }
  const words = f.trim().split(' ').filter(Boolean);
  for (const w of words) {
    if (FILLER.has(w)) continue;
    const pos = POSITION_ORDER.find(k => POSITION_WORDS[k].some(e => !e.includes(' ') && wordMatches(w, e)));
    if (pos) { positions.add(pos); continue; }
    const feat = FEATURE_ORDER.find(k => FEATURE_WORDS[k].some(e => !e.includes(' ') && wordMatches(w, e)));
    if (feat) { features.add(feat); continue; }
    return null;
  }
  const out: VocabTag[] = [
    ...FEATURE_ORDER.filter(k => features.has(k)).map(key => ({ kind: 'feature' as const, key })),
    ...normalizePositions(positions).map(key => ({ kind: 'position' as const, key })),
  ];
  return out.length ? out : null;
};
/** A value that is exactly ONE vocabulary tag ("Left" → left). */
const vocabularyOf = (value: string): VocabTag | null => {
  const tags = vocabularyTagsOf(value);
  return tags && tags.length === 1 ? tags[0] : null;
};

/** ONE tag row for a card: functionality, answer, features, positions — no
 *  tag twice. */
export const cardTags = (i: CardTagInput): PartTag[] => {
  const out: PartTag[] = [];
  const seen = new Set<string>();
  const seenText = new Set<string>();
  const push = (t: PartTag, text?: string) => {
    const id = tagId(t);
    const ft = text ? foldText(text).trim() : '';
    if (seen.has(id) || (ft && seenText.has(ft))) return;
    seen.add(id);
    if (ft) seenText.add(ft);
    out.push(t);
  };
  const requested = (i.requestedType || '').trim();
  const fnValue = i.isRequested && requested ? sentence(requested) : (i.functionName || '').trim();
  if (fnValue) push({ kind: 'function', key: foldText(fnValue).trim(), match: i.isRequested, value: fnValue }, fnValue);
  // A row the catalog flags as a kit, or whose own name says so ("SERVICE
  // KIT-OIL FILTER", "JUEGO DE PASTILLAS"), IS a kit: it wears the kit tag,
  // never "Kit part" (that is a component of one — part_of_building_kit).
  const kitNamed = mentions(foldText(i.texts.filter(Boolean).join(' ')), FEATURE_WORDS.kitPart);
  if (i.isKit || kitNamed) push({ kind: 'kit', key: 'kit', n: 0 });
  // An answer made only of side / position / feature words ("Left front")
  // is those tags, shown separately — not one combined answer tag.
  const answerVocab = i.answer ? vocabularyTagsOf(i.answer.label) : null;
  const answer = answerVocab ? null : i.answer;
  const answerFolded = answer ? foldText(answer.label) : '';
  if (answer) push({ kind: 'answer', key: String(answer.n), n: answer.n, value: answer.label }, answer.label);

  const features = new Set<FeatureKey>(featuresOf(i.texts, i.extra_details));
  // The backend read the row's own text (any language, any catalog format);
  // the word lists are only for a row that has no reading yet.
  const fromAxes = positionsFromAxes(i.axes);
  const positions = new Set<PositionKey>(fromAxes.length ? fromAxes : positionsOf(i.texts, i.extra_details));
  for (const v of answerVocab || []) { if (v.kind === 'position') positions.add(v.key); else features.add(v.key); }
  // The AI's side / position only fills a gap: what the catalog printed
  // ("…SIDE,RH") is a fact, the AI's "both" a reading of it. Letting "both"
  // in beside "right" erased "right" (normalizePositions), so a part the
  // catalog calls right-hand stopped answering the "Right" filter
  // (dev 2026-10-17: a side question with zero parts under its own answer).
  // And once the catalog's own text HAS been read (axes), its silence is a
  // fact too: a row the reader gave no side / position gets none from the
  // AI, which only knows the diagram's title (live 2026-10-18: a "Front" tag
  // on an engine oil filter, from a "FRONT CASE & OIL FILTER" section).
  const read = !!i.axes;
  const hasSide = read || positions.has('left') || positions.has('right') || positions.has('bothSides');
  const hasEnd = read || positions.has('front') || positions.has('rear');
  if (i.ai?.side && !hasSide) positions.add(i.ai.side);
  if (i.ai?.position && !hasEnd) positions.add(i.ai.position);
  const aiFree: Array<{ key: string; value: string }> = [];
  const sideKeys: PositionKey[] = ['left', 'right', 'bothSides'];
  const endKeys: PositionKey[] = ['front', 'rear'];
  const stated = (keys: PositionKey[]) => read || keys.some(k => positions.has(k));
  for (const raw of i.ai?.features || []) {
    const { label, value } = splitAiTag(raw);
    if (!value) continue;
    // "Left front" from the AI is the left and front tags, not a third one.
    // Like ai.side / ai.position, an AI side or end word only fills a gap:
    // the row's own reading ("…SIDE,RH", a rear-bumper section) stands, and
    // an AI "front" never lands on a rear part (dev 2026-10-17: a "Front"
    // chip on a rear-bumper question that matched nothing).
    const vocab = vocabularyTagsOf(value);
    if (vocab) {
      for (const v of vocab) {
        if (v.kind !== 'position') { features.add(v.key); continue; }
        if (sideKeys.includes(v.key) && stated(sideKeys)) continue;
        if (endKeys.includes(v.key) && stated(endKeys)) continue;
        positions.add(v.key);
      }
      continue;
    }
    aiFree.push({ key: `ai:${foldText(value).trim()}`, value: label ? `${label}: ${value}` : value });
  }
  // An answer that names a feature IS that feature ("② Complete mirror"
  // replaces "Complete assembly"); same for an AI value it restates.
  for (const k of FEATURE_ORDER) {
    if (!features.has(k)) continue;
    if (k === 'kitPart' && (i.isKit || kitNamed)) continue;
    if (answerFolded && mentions(answerFolded, FEATURE_WORDS[k])) continue;
    push({ kind: 'feature', key: k });
  }
  // Words the card already states: its own names and the vocabulary tags it
  // wears. An AI value made only of those ("Right stay" on STAY-RR BUMPER RH,
  // "Center upper bracket" on BRACKET-RR BUMPER CTR,UPR) restates the card
  // — it is not a second tag, and as a filter it would match that one card.
  const known = new Set<string>();
  for (const t of i.texts) for (const w of foldText(t).trim().split(' ')) if (w) known.add(stem(w));
  const isVocab = (w: string) =>
    POSITION_ORDER.some(k => POSITION_WORDS[k].some(e => !e.includes(' ') && wordMatches(w, e)))
    || FEATURE_ORDER.some(k => FEATURE_WORDS[k].some(e => !e.includes(' ') && wordMatches(w, e)));
  // Restates when EVERY word is one the card states (a name word or a side /
  // height / feature word). One new word ("Full kit" on a repair kit) is a
  // fact and stays; a per-part label that is still unique to its card is kept
  // off the filter row by filterTags (a one-card chip filters nothing).
  const restates = (value: string): boolean => {
    const words = foldText(value).trim().split(' ').filter(w => w && !FILLER.has(w));
    return words.length > 0 && words.every(w => known.has(stem(w)) || isVocab(w));
  };
  for (const f of aiFree) {
    const fv = foldText(splitAiTag(f.value).value);
    if (answerFolded && (answerFolded.includes(fv) || fv.includes(answerFolded))) continue;
    if (foldText(fnValue).includes(fv)) continue;
    if (restates(splitAiTag(f.value).value)) continue;
    push({ kind: 'feature', key: f.key, value: f.value }, splitAiTag(f.value).value);
  }
  // Same for a position the answer names ("① Left" replaces "Left").
  for (const k of normalizePositions(positions)) {
    if (answerFolded && mentions(answerFolded, POSITION_WORDS[k])) continue;
    push({ kind: 'position', key: k });
  }
  return out;
};

/** Tags every list has (by id), in the first list's order. */
export const commonTags = (lists: PartTag[][]): PartTag[] => {
  if (!lists.length) return [];
  const [first, ...rest] = lists;
  return first.filter(t => rest.every(l => l.some(x => tagId(x) === tagId(t))));
};

/** A kit's header: the kit itself, then every tag true of the WHOLE kit —
 *  what all its versions (the kit SKUs) say about themselves, and whatever
 *  every component shares. When a component is the requested part but the
 *  kit SKU isn't, the header says the kit includes it. */
export const kitHeaderTags = (kitTags: PartTag[][], memberTags: PartTag[][], requestedType?: string | null): PartTag[] => {
  const out: PartTag[] = [{ kind: 'kit', key: 'kit', n: memberTags.length }];
  const seen = new Set<string>(['kit']);
  const add = (t: PartTag) => { const id = tagId(t); if (!seen.has(id)) { seen.add(id); out.push(t); } };
  const fromKits = commonTags(kitTags);
  const kitFn = fromKits.find(t => t.kind === 'function');
  const requested = (requestedType || '').trim();
  const memberMatches = memberTags.some(l => l.some(t => t.kind === 'function' && t.match));
  if (kitFn && kitFn.kind === 'function' && !kitFn.match && memberMatches && requested) {
    add({ kind: 'function', key: foldText(requested).trim(), match: true, includes: true, value: sentence(requested) });
  } else if (kitFn) add(kitFn);
  // "Kit part" is what the header's kit tag already says.
  const kitOwn = (t: PartTag) => t.kind === 'feature' && t.key === 'kitPart';
  for (const t of fromKits) if (t.kind !== 'function' && !kitOwn(t)) add(t);
  // A functionality tag shared by every component describes them, not the kit.
  for (const t of commonTags(memberTags)) if (t.kind !== 'function' && !kitOwn(t)) add(t);
  return out;
};

/** A row's tags minus what its header already shows (rule 2). `isKit`: the
 *  row is a version of the kit itself, whose functionality the header states
 *  ("Includes mirror base" / the kit's own) — it doesn't say it again. */
export const withoutTags = (tags: PartTag[], header: PartTag[] | null | undefined, isKit = false): PartTag[] => {
  if (!header?.length) return tags;
  if (isKit && header.some(t => t.kind === 'function')) tags = tags.filter(t => t.kind !== 'function');
  const ids = new Set(header.map(tagId));
  const underKit = ids.has('kit');
  // Under a kit header "Kit part" is the header's kit tag again. The header's
  // "includes <type>" is not a component's own functionality: each component
  // still says what it is.
  return tags.filter(t => !ids.has(tagId(t)) && !(underKit && t.kind === 'feature' && t.key === 'kitPart'));
};

// ── Filters ─────────────────────────────────────────────────────────────────

const KIND_ORDER: PartTag['kind'][] = ['function', 'answer', 'feature', 'position', 'kit'];

/** The tag a card's tag answers to as a FILTER. "Includes mirror base" (a
 *  kit) and "Mirror base" (the part) are one filter; so are the answer
 *  "① Left" and the position "Left" — one "Left" chip, not two. */
const filterTag = (t: PartTag): PartTag => {
  if (t.kind === 'function') return { ...t, includes: false };
  if (t.kind === 'answer') {
    const vocab = vocabularyOf(t.value);
    if (vocab) return vocab;
  }
  return t;
};

/** The filter chips for a list: every positive tag some (not all) cards wear,
 *  grouped by type. `active` ids stay listed even when they no longer split. */
/** The position axis a key belongs to (left / right / both = side; front / rear = end …). */
const AXIS_OF: Record<PositionKey, string> = {
  left: 'side', right: 'side', bothSides: 'side', front: 'end', rear: 'end', upper: 'height', lower: 'height', outer: 'placement', inner: 'placement',
};

/** The axes the request's own words settle ("Bumper, Rear" settles the end
 *  axis) — read with the same vocabulary the tags use. */
export const settledAxesOf = (texts: Array<string | null | undefined>): Set<string> => {
  const out = new Set<string>();
  // Whole value first ("Rear"), then each comma piece of a label ("Bumper, Rear").
  for (const t of texts) {
    const pieces = [String(t || ''), ...String(t || '').split(/[,;/]/)];
    for (const piece of pieces) for (const v of vocabularyTagsOf(piece) || []) if (v.kind === 'position') out.add(AXIS_OF[v.key]);
  }
  return out;
};

// Positions the request already names ("Door Handle, Front, Right, Outer")
// stay chips: the drawing holds the other side too, and "Right" is how the
// customer narrows it back. (They used to be dropped as settled; customers
// read that as the position filters going missing.)
export const filterTags = (cards: PartTag[][], active: Set<string> = new Set()): PartTag[] => {
  const counts = new Map<string, { tag: PartTag; n: number; first: number }>();
  cards.forEach(tags => {
    const ids = new Set<string>();
    for (const t of tags) {
      if (isNegativeTag(t)) continue;
      // "Both sides" is a fact about the part (it answers Left AND Right),
      // never a chip: as a filter it would hide the sided parts and show
      // nothing a customer asks for.
      if (t.kind === 'position' && t.key === 'bothSides') continue;
      // "Includes mirror base" (a kit) and "Mirror base" (the part) are one
      // filter: both answer "show me the mirror bases".
      const tag = filterTag(t);
      const id = tagId(tag);
      if (ids.has(id)) continue;
      ids.add(id);
      const c = counts.get(id);
      if (c) c.n += 1; else counts.set(id, { tag, n: 1, first: counts.size });
    }
  });
  // A chip must split the list: not one every card wears, and — for an AI
  // value, which describes one part — not one a single card wears either
  // (the card already shows it; as a filter it only hides the rest).
  const single = (id: string, c: { tag: PartTag; n: number }) =>
    c.n === 1 && cards.length > 1 && c.tag.kind === 'feature' && c.tag.key.startsWith('ai:') && !active.has(id);
  return Array.from(counts.entries())
    .filter(([id, c]) => (c.n < cards.length || active.has(id)) && !single(id, c))
    .sort((a, b) => KIND_ORDER.indexOf(a[1].tag.kind) - KIND_ORDER.indexOf(b[1].tag.kind) || a[1].first - b[1].first)
    .map(([, c]) => c.tag);
};

/** The id a card's tag answers to in the filter (see filterTags). */
export const filterId = (t: PartTag): string => tagId(filterTag(t));

/** Does a card pass the active filters? Every chip must be on it (AND). */
export const passesFilters = (tags: PartTag[], active: Set<string>): boolean => {
  if (!active.size) return true;
  return Array.from(active).every(id => cardAnswersFilter(tags, id));
};

/** The position axis a filter id sits on ("position:left" → "side"), or null. */
export const filterAxisOf = (id: string): string | null => {
  const m = /^position:(.+)$/.exec(id);
  return (m && AXIS_OF[m[1] as PositionKey]) || null;
};

/** Toggle one chip. Positions on one axis are alternatives, not a
 *  combination: "Left" while "Right" is on switches to Left — AND-ing the two
 *  could only empty the list. Every other chip combines with AND. */
export const toggleFilterId = (active: Set<string>, id: string): Set<string> => {
  const next = new Set(active);
  if (next.has(id)) { next.delete(id); return next; }
  const axis = filterAxisOf(id);
  if (axis) Array.from(next).forEach(x => { if (filterAxisOf(x) === axis) next.delete(x); });
  next.add(id);
  return next;
};

/** The filter ids a card answers to: its own tags, and — for a part that
 *  fits both sides — "Left" and "Right" too, since "both" means both. */
export const cardFilterIds = (tags: PartTag[]): Set<string> => {
  const ids = new Set(tags.filter(t => !isNegativeTag(t)).map(filterId));
  if (ids.has('position:bothSides')) { ids.add('position:left'); ids.add('position:right'); }
  return ids;
};
export const cardAnswersFilter = (tags: PartTag[], id: string): boolean => cardFilterIds(tags).has(id);

/** ① ② ③ … for the answer tag. */
export const circled = (n: number): string => (n >= 1 && n <= 20 ? String.fromCharCode(0x2460 + n - 1) : `(${n})`);

// ── Answer options from the AI comparison ───────────────────────────────────

export interface HintFactorLite {
  title: string;
  per_option?: Array<{ option_id: string; text: string }> | null;
}

export interface AnswerOption { n: number; label: string; optionIds: string[] }

/** The comparison factor that splits the candidates into 2–6 answers
 *  (preferring the one the AI named as THE differentiator). Each distinct
 *  value is an answer; the candidates carrying it satisfy it. */
export const answerOptionsFrom = (
  factors: HintFactorLite[] | null | undefined,
  optionIds: string[],
  differentiator?: string | null,
): { title: string; answers: AnswerOption[] } | null => {
  if (!factors?.length || optionIds.length < 2) return null;
  const ids = new Set(optionIds);
  const diff = foldText(differentiator || '').trim();
  const scored = factors.map((f, idx) => {
    const groups = new Map<string, { label: string; ids: string[] }>();
    for (const po of f.per_option || []) {
      if (!ids.has(po.option_id)) continue;
      const text = String(po.text || '').trim();
      if (!text) continue;
      const k = foldText(text);
      const g = groups.get(k) ?? { label: text.charAt(0).toUpperCase() + text.slice(1), ids: [] };
      if (!g.ids.includes(po.option_id)) g.ids.push(po.option_id);
      groups.set(k, g);
    }
    const covered = Array.from(groups.values()).reduce((a, g) => a + g.ids.length, 0);
    const title = foldText(f.title).trim();
    const named = !!diff && !!title && (diff.includes(title) || title.includes(diff));
    return { f, idx, groups: Array.from(groups.values()), covered, named };
  }).filter(s => s.groups.length >= 2 && s.groups.length <= 6);
  if (!scored.length) return null;
  scored.sort((a, b) => Number(b.named) - Number(a.named) || b.covered - a.covered || a.groups.length - b.groups.length || a.idx - b.idx);
  const best = scored[0];
  return { title: best.f.title, answers: best.groups.map((g, i) => ({ n: i + 1, label: g.label, optionIds: g.ids })) };
};

/** An answer that keeps every part the other answers keep ("Both", "Multiple
 *  parts"). It narrows nothing, so it never tags a part or becomes a filter
 *  chip — and, unless the backend generated it as a "Both" (`multi`), it is
 *  not offered as
 *  an answer at all. */
export const isCatchAllAnswer = (answer: { optionIds: string[] }, answers: Array<{ optionIds: string[] }>): boolean => {
  const others = answers.filter(a => a !== answer);
  if (others.length < 2 || !answer.optionIds.length) return false;
  const mine = new Set(answer.optionIds);
  const rest = new Set(others.flatMap(a => a.optionIds));
  return rest.size > 0 && Array.from(rest).every(id => mine.has(id));
};


/** "Base De Espejo Retrovisor, Ambos, Exterior" → "base de espejo retrovisor". */
export const requestedTypeOf = (label: string | null | undefined): string => functionOf(label).toLowerCase();

// ── Search ──────────────────────────────────────────────────────────────────
// ONE search box. A query with a digit is tried as a part number: one run of
// letters and digits with spacing and punctuation ignored, so "A002811" and
// "A 002 811" both land on A 002 811 03 14 (and on a replacement number the
// row carries), or as the callout ref itself ("30", "#30"). A phrase matches
// when every meaningful word starts a word the part is described by — its
// name, translation, description and tags — in any order, case and accents
// folded. A query that is both ("bolt M6") lands on either.

const SEARCH_STOPWORDS = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'the', 'of', 'a', 'an', 'and', 'y', 'e', 'o', 'do', 'da', 'dos', 'das', 'para', 'for', 'con', 'com', 'with', 'en', 'em', 'in']);
const alnum = (s: string | null | undefined) => String(s || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

export interface Searchable {
  /** Everything the card says in words (any language). */
  texts: Array<string | null | undefined>;
  /** Its part number and the numbers in its replacement chain. */
  numbers: Array<string | null | undefined>;
  callout?: string | null;
}

/** The meaningful words of a query (folded, no stopwords). */
export const searchWords = (q: string): string[] =>
  foldText(q).trim().split(' ').filter(w => w && !SEARCH_STOPWORDS.has(w));

export const searchMatches = (item: Searchable, query: string): boolean => {
  const q = String(query || '').trim();
  if (!q) return true;
  // "#30" asks for the ref only.
  if (q.startsWith('#')) return !!item.callout && alnum(item.callout) === alnum(q.slice(1));
  if (/\d/.test(q)) {
    if (item.callout && alnum(item.callout) === alnum(q)) return true;
    const run = alnum(q);
    if (run && item.numbers.some(n => alnum(n).includes(run))) return true;
  }
  const ws = searchWords(q);
  // Only filler words ("de", "the") narrow nothing.
  if (!ws.length) return true;
  const hay = foldText(item.texts.filter(Boolean).join(' '));
  return ws.every(w => hay.includes(` ${w}`));
};
